import type { StudioLineEntry, StudioLineSet } from 'src/components/star-grid/reportStudio.types';

const depth = (entry: StudioLineEntry): number => entry.indent ?? 0;

/** The member a line asks the report for: its element id, else the label it was typed with. */
export const lineMember = (entry: StudioLineEntry): string => entry.member_id?.trim() || entry.label?.trim() || '';

/** The key a line's expansion override is stored under; the same member the spec states for it. */
const expansionKey = (entry: StudioLineEntry): string => lineMember(entry) || entry.id;

/** The member lines that head others: the next drawn line sits deeper. A spacer is drawn, not a child. */
export const lineNodeIds = (entries: readonly StudioLineEntry[]): ReadonlySet<string> => {
  const nodes: string[] = [];
  let nextDepth: number | undefined;
  for (let index = entries.length - 1; index >= 0; index -= 1) {
    const entry = entries[index];
    if (entry.kind === 'spacer') continue;
    if (entry.kind === 'member' && nextDepth !== undefined && nextDepth > depth(entry)) nodes.push(entry.id);
    nextDepth = depth(entry);
  }
  return new Set(nodes.reverse());
};

/** Whether the set states its expansion at all; an unconfigured set leaves the report's own behaviour and hides no line. */
export const isExpansionConfigured = (set: StudioLineSet): boolean =>
  set.expand_default !== undefined || Boolean(set.expanded_members?.length) || Boolean(set.collapsed_members?.length);

/** The default a set opens with: as stated, else open, which is how an unconfigured set already reads. */
export const expandDefaultOf = (set: StudioLineSet): boolean => set.expand_default ?? !isExpansionConfigured(set);

/** Whether the set lists its expandable rows explicitly rather than leaving them derived. */
export const expandableMembersExplicit = (set: StudioLineSet): boolean => Array.isArray(set.expandable_members);

/** Whether a line may expand: as listed when the set states it, else derived -- a node that heads others. */
export const isLineExpandable = (set: StudioLineSet, entry: StudioLineEntry): boolean =>
  Array.isArray(set.expandable_members) ? set.expandable_members.includes(expansionKey(entry)) : lineNodeIds(set.entries).has(entry.id);

/** The member keys this set would store as expandable: the explicit list, else the derived member nodes. */
export const expandableMembersOf = (set: StudioLineSet): string[] => {
  if (Array.isArray(set.expandable_members)) return [...set.expandable_members];
  const nodes = lineNodeIds(set.entries);
  return set.entries
    .filter((entry) => nodes.has(entry.id))
    .map(expansionKey)
    .filter(Boolean);
};

/**
 * Whether a node line is open in the editor. Open keeps its child lines in the report; closed stores the node at parent
 * level -- its children reach the report only behind the node's expand icon.
 */
export const isLineExpanded = (set: StudioLineSet, entry: StudioLineEntry): boolean => {
  const key = expansionKey(entry);
  if (set.collapsed_members?.includes(key)) return false;
  if (set.expanded_members?.includes(key)) return true;
  return expandDefaultOf(set);
};

/** The ids of lines beneath a closed node that `closes` picks: every deeper line until one at its depth or shallower. */
const linesUnder = (set: StudioLineSet, closes: (entry: StudioLineEntry) => boolean): ReadonlySet<string> => {
  const hidden = new Set<string>();
  if (!isExpansionConfigured(set)) return hidden;
  const nodes = lineNodeIds(set.entries);
  let hideDeeperThan: number | undefined;
  for (const entry of set.entries) {
    if (hideDeeperThan !== undefined && entry.kind !== 'spacer' && depth(entry) <= hideDeeperThan) hideDeeperThan = undefined;
    if (hideDeeperThan !== undefined) {
      hidden.add(entry.id);
      continue;
    }
    if (nodes.has(entry.id) && !isLineExpanded(set, entry) && closes(entry)) hideDeeperThan = depth(entry);
  }
  return hidden;
};

/** The lines the editor hides: those under any closed node. */
export const hiddenLineIds = (set: StudioLineSet): ReadonlySet<string> => linesUnder(set, () => true);

/** The lines the report leaves out: those under a closed node with no expand icon, which reads at parent level. */
export const reportOmittedLineIds = (set: StudioLineSet): ReadonlySet<string> => linesUnder(set, (entry) => !isLineExpandable(set, entry));

/** The set with one node's state stated, the default made explicit and an override only where it differs. */
export const withLineExpanded = (set: StudioLineSet, entry: StudioLineEntry, expanded: boolean): StudioLineSet => {
  const key = expansionKey(entry);
  const expandDefault = expandDefaultOf(set);
  const others = (members: readonly string[] = []) => members.filter((item) => item !== key);
  const differs = expanded !== expandDefault;
  return {
    ...set,
    // Icons stated too, so the report honours this edit instead of keeping its imported screen's look.
    expandable_members: expandableMembersOf(set),
    expand_default: expandDefault,
    expanded_members: [...others(set.expanded_members), ...(differs && expanded ? [key] : [])],
    collapsed_members: [...others(set.collapsed_members), ...(differs && !expanded ? [key] : [])]
  };
};

/** The set with its expand icons stated explicitly, one node's toggled. Whether the node is open stays as it was. */
export const withLineExpandable = (set: StudioLineSet, entry: StudioLineEntry, expandable: boolean): StudioLineSet => {
  const key = expansionKey(entry);
  const members = new Set(expandableMembersOf(set));
  if (expandable) members.add(key);
  else members.delete(key);
  return { ...set, expandable_members: [...members] };
};
